<script setup lang="ts">
import { computed } from 'vue'
import type { FaqActionInput, FaqActionKind } from '@/stores/useFaqAdminStore'
import { useFeatureRegistryAdminStore } from '@/stores/useFeatureRegistryAdminStore'
import { TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'

interface Props {
  modelValue: FaqActionInput
  index: number
  isFirst: boolean
  isLast: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [FaqActionInput]
  remove: []
  moveUp: []
  moveDown: []
}>()

const registryStore = useFeatureRegistryAdminStore()

const action = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

function updateField<K extends keyof FaqActionInput>(key: K, value: FaqActionInput[K]) {
  emit('update:modelValue', { ...action.value, [key]: value })
}

function updateLabel(lang: 'fr' | 'en', value: string) {
  const labels = { ...action.value.labels, [lang]: value }
  if (!value) delete labels[lang]
  emit('update:modelValue', { ...action.value, labels })
}

function updateDeniedMessage(lang: 'fr' | 'en', value: string) {
  const deniedMessages = { ...(action.value.deniedMessages ?? {}) }
  if (value) {
    deniedMessages[lang] = value
  } else {
    delete deniedMessages[lang]
  }
  emit('update:modelValue', {
    ...action.value,
    deniedMessages: Object.keys(deniedMessages).length ? deniedMessages : undefined,
  })
}

const kindOptions: { value: FaqActionKind; label: string }[] = [
  { value: 'OPEN_PAGE', label: 'Ouvrir une page interne' },
  { value: 'TRIGGER_CAPABILITY', label: 'Déclencher une action (capability)' },
  { value: 'EXTERNAL_LINK', label: 'Lien externe' },
]

// Liste des Pages disponibles depuis le Feature Registry (pour autocomplete OPEN_PAGE)
const availablePages = computed(() =>
  registryStore.pages.map((p) => ({ key: p.key, label: `${p.key} — ${p.label}` })),
)

// Liste des Capabilities disponibles (Features type=CAPABILITY)
const availableCapabilities = computed(() =>
  registryStore.features
    .filter((f) => f.type === 'CAPABILITY')
    .map((f) => ({ key: f.key, label: `${f.key} — ${f.label}` })),
)
</script>

<template>
  <div class="border border-base-300 rounded-lg p-3 bg-base-100 space-y-3">
    <!-- Header avec ordre + boutons -->
    <div class="flex items-center justify-between gap-2">
      <div class="text-xs font-semibold text-base-content/60">Action #{{ index + 1 }}</div>
      <div class="flex items-center gap-1">
        <button
          class="btn btn-ghost btn-xs"
          :disabled="isFirst"
          @click="emit('moveUp')"
          title="Monter"
        >
          <ChevronUpIcon class="w-3 h-3" />
        </button>
        <button
          class="btn btn-ghost btn-xs"
          :disabled="isLast"
          @click="emit('moveDown')"
          title="Descendre"
        >
          <ChevronDownIcon class="w-3 h-3" />
        </button>
        <button
          class="btn btn-ghost btn-xs text-error"
          @click="emit('remove')"
          title="Supprimer"
        >
          <TrashIcon class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- Kind selector -->
    <div class="form-control">
      <label class="label py-1"><span class="label-text text-xs">Type d'action</span></label>
      <select
        :value="action.kind"
        @change="updateField('kind', ($event.target as HTMLSelectElement).value as FaqActionKind)"
        class="select select-bordered select-sm"
      >
        <option v-for="opt in kindOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </div>

    <!-- Labels par langue -->
    <div class="grid grid-cols-2 gap-2">
      <div class="form-control">
        <label class="label py-1">
          <span class="label-text text-xs">Label FR</span>
          <span class="label-text-alt text-error">*</span>
        </label>
        <input
          type="text"
          :value="action.labels.fr ?? ''"
          @input="updateLabel('fr', ($event.target as HTMLInputElement).value)"
          placeholder="Ex: Voir mes factures"
          class="input input-bordered input-sm"
        />
      </div>
      <div class="form-control">
        <label class="label py-1"><span class="label-text text-xs">Label EN (optionnel)</span></label>
        <input
          type="text"
          :value="action.labels.en ?? ''"
          @input="updateLabel('en', ($event.target as HTMLInputElement).value)"
          placeholder="View my invoices"
          class="input input-bordered input-sm"
        />
      </div>
    </div>

    <!-- Cible selon kind -->
    <div v-if="action.kind === 'OPEN_PAGE'" class="form-control">
      <label class="label py-1">
        <span class="label-text text-xs">Page cible (key)</span>
        <span class="label-text-alt text-error">*</span>
      </label>
      <input
        type="text"
        :value="action.targetPageKey ?? ''"
        @input="updateField('targetPageKey', ($event.target as HTMLInputElement).value || null)"
        :list="`pages-${index}`"
        placeholder="ex: invoices.list"
        class="input input-bordered input-sm font-mono"
      />
      <datalist :id="`pages-${index}`">
        <option v-for="p in availablePages" :key="p.key" :value="p.key">{{ p.label }}</option>
      </datalist>
    </div>

    <div v-else-if="action.kind === 'TRIGGER_CAPABILITY'" class="form-control">
      <label class="label py-1">
        <span class="label-text text-xs">Capability cible (key)</span>
        <span class="label-text-alt text-error">*</span>
      </label>
      <input
        type="text"
        :value="action.targetCapabilityKey ?? ''"
        @input="updateField('targetCapabilityKey', ($event.target as HTMLInputElement).value || null)"
        :list="`capabilities-${index}`"
        placeholder="ex: billing.invoices.download-pdf"
        class="input input-bordered input-sm font-mono"
      />
      <datalist :id="`capabilities-${index}`">
        <option v-for="c in availableCapabilities" :key="c.key" :value="c.key">{{ c.label }}</option>
      </datalist>
    </div>

    <div v-else-if="action.kind === 'EXTERNAL_LINK'" class="form-control">
      <label class="label py-1">
        <span class="label-text text-xs">URL externe</span>
        <span class="label-text-alt text-error">*</span>
      </label>
      <input
        type="url"
        :value="action.externalUrl ?? ''"
        @input="updateField('externalUrl', ($event.target as HTMLInputElement).value || null)"
        placeholder="https://..."
        class="input input-bordered input-sm"
      />
    </div>

    <!-- Denied messages (optionnel) -->
    <details class="collapse collapse-arrow bg-base-200">
      <summary class="collapse-title text-xs py-2 min-h-0">
        Messages si refusé (optionnel)
      </summary>
      <div class="collapse-content space-y-2 text-xs">
        <div class="form-control">
          <label class="label py-1"><span class="label-text text-xs">Message FR</span></label>
          <textarea
            :value="action.deniedMessages?.fr ?? ''"
            @input="updateDeniedMessage('fr', ($event.target as HTMLTextAreaElement).value)"
            rows="2"
            placeholder="Vous n'avez pas le droit nécessaire. Contactez votre administrateur."
            class="textarea textarea-bordered textarea-sm"
          />
        </div>
        <div class="form-control">
          <label class="label py-1"><span class="label-text text-xs">Message EN</span></label>
          <textarea
            :value="action.deniedMessages?.en ?? ''"
            @input="updateDeniedMessage('en', ($event.target as HTMLTextAreaElement).value)"
            rows="2"
            class="textarea textarea-bordered textarea-sm"
          />
        </div>
      </div>
    </details>
  </div>
</template>
